import { Box, Flex, RadioGroup, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { LuCheck, LuChevronLeft } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useBrand } from "brand/BrandConfig";
import { useAuthStore } from "stores/AuthStoreProvider";
import { GradientButton } from "src/components/GradientButton";
import { formatDate, membershipSummary } from "./membershipSummary";

type Step = "overview" | "reason" | "done";

/**
 * Membership status and the in-app cancel flow: pick a reason, confirm,
 * done. No retention offers: cancelling takes two taps after "Cancel".
 */
export const MembershipPage = observer(() => {
  const brand = useBrand();
  const authStore = useAuthStore();
  const navigate = useNavigate();
  const user = authStore.user;
  const [step, setStep] = React.useState<Step>("overview");
  const [reasonID, setReasonID] = React.useState<string | null>(null);
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (!user) return null;

  async function cancel() {
    const reason = brand.cancellationReasons.find((r) => r.id === reasonID);
    if (!reason) return;
    setIsCancelling(true);
    setError(null);
    try {
      await authStore.cancelSubscription({
        cancelReason: `${reason.id}:${reason.text}`,
      });
      setStep("done");
    } catch (err) {
      console.error(err);
      setError(
        `Something went wrong. Please try again, or email ${brand.email} and we'll cancel it for you.`,
      );
    } finally {
      setIsCancelling(false);
    }
  }

  const endsAt = formatDate(user.subscriptionEndsAt);
  const card = {
    bg: "surface",
    borderRadius: "22px",
    p: 5,
    mx: 4,
    mt: 4,
    animation: "tlRise 0.35s ease both",
  };

  return (
    <Box pb="calc(24px + env(safe-area-inset-bottom))">
      <Flex
        align="center"
        gap={1}
        px={2}
        pt="calc(8px + env(safe-area-inset-top))"
      >
        <Box
          as="button"
          aria-label="Back"
          onClick={() => navigate("/settings")}
          p={2}
          borderRadius="full"
          _active={{ bg: "elevated" }}
        >
          <LuChevronLeft size={26} />
        </Box>
        <Text fontWeight="semibold" fontSize="lg">
          Membership
        </Text>
      </Flex>

      {step === "overview" && (
        <Box {...card}>
          <Text
            fontSize="xs"
            color="faint"
            textTransform="uppercase"
            letterSpacing="0.06em"
            fontWeight="semibold"
          >
            Your plan
          </Text>
          <Text fontSize="xl" fontWeight="bold" mt={1}>
            {brand.name} membership
          </Text>
          <Text color="muted" mt={1} mb={6}>
            {membershipSummary(user)}
          </Text>
          {user.isMembershipManageable ? (
            <Box
              as="button"
              width="100%"
              h="50px"
              borderRadius="full"
              bg="elevated"
              color="red.300"
              fontWeight="semibold"
              onClick={() => setStep("reason")}
              _active={{ transform: "scale(0.98)" }}
              transition="transform 0.15s"
            >
              Cancel membership
            </Box>
          ) : (
            <Text fontSize="sm" color="muted">
              Questions about billing? Email {brand.email}.
            </Text>
          )}
        </Box>
      )}

      {step === "reason" && (
        <Box {...card}>
          <Text fontSize="xl" fontWeight="bold">
            Why are you leaving?
          </Text>
          <Text fontSize="sm" color="muted" mt={1} mb={5}>
            Pick one, then confirm below.
          </Text>
          <RadioGroup.Root
            value={reasonID}
            onValueChange={(e) => setReasonID(e.value)}
            colorPalette="red"
            mb={6}
          >
            <Flex direction="column" gap={4}>
              {brand.cancellationReasons.map((r) => (
                <RadioGroup.Item key={r.id} value={r.id}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{r.text}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </Flex>
          </RadioGroup.Root>
          {error && (
            <Text color="red.300" fontSize="sm" mb={3}>
              {error}
            </Text>
          )}
          <GradientButton
            onClick={cancel}
            disabled={!reasonID}
            loading={isCancelling}
            loadingText="Cancelling…"
          >
            Confirm cancellation
          </GradientButton>
          <Box
            as="button"
            width="100%"
            h="48px"
            mt={2}
            color="muted"
            fontWeight="medium"
            onClick={() => setStep("overview")}
          >
            Keep my membership
          </Box>
        </Box>
      )}

      {step === "done" && (
        <Flex
          {...card}
          direction="column"
          align="center"
          textAlign="center"
          gap={2}
          py={8}
        >
          <Flex
            boxSize="56px"
            borderRadius="full"
            bg="elevated"
            align="center"
            justify="center"
            color="#3ccf6e"
            mb={2}
          >
            <LuCheck size={28} />
          </Flex>
          <Text fontSize="xl" fontWeight="bold">
            Membership cancelled
          </Text>
          <Text color="muted">
            {endsAt
              ? `You won't be charged again. You can keep using ${brand.name} until ${endsAt}.`
              : "You won't be charged again."}
          </Text>
        </Flex>
      )}
    </Box>
  );
});
